import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useI18n } from "../../../hooks/useTranslations";
import { useMaintenanceTypesApi } from "../../../http/maintenanceTypes/api";
import { UpsertMaintenanceType } from "../../../http/maintenanceTypes/types";
import ModalDialog from "../../ui/ModalDialog";
import MaintenanceTypeForm, { MaintenanceTypeFormBody } from "./MaintenanceTypeForm";

type EditMaintenanceTypeDialogProps = {
  maintenanceTypeId: string;
  onClose: () => void;
};

const EditMaintenanceTypeDialog: FC<EditMaintenanceTypeDialogProps> = ({
  maintenanceTypeId,
  onClose,
}: EditMaintenanceTypeDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const maintenanceTypesApi = useMaintenanceTypesApi();

  const maintenanceTypeSwr = maintenanceTypesApi.getMaintenanceType(maintenanceTypeId, aborter.current.signal);
  const [maintenanceType, isInitialized] = useDataInitializer(maintenanceTypeSwr.data, maintenanceTypeSwr.error);

  useEffect(() => {
    maintenanceTypeId && maintenanceTypeSwr.trigger();
  }, [maintenanceTypeId]);

  useEffect(() => {
    maintenanceTypeSwr.error && dispatch(showErrorNotification(i18n, maintenanceTypeSwr.error));
  }, [maintenanceTypeSwr.error]);

  const onSubmit = async (formData: MaintenanceTypeFormBody): Promise<void> => {
    if (maintenanceType?.id) {
      const body: UpsertMaintenanceType = {
        name: formData.name,
      };

      setIsSubmitting(true);
      try {
        await maintenanceTypesApi.updateAdminMaintenanceType(maintenanceType.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.maintenanceTypes.edit}>
      <MaintenanceTypeForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.maintenanceTypes.update || isSubmitting || !maintenanceType?.id}
        isLoading={!isInitialized}
        maintenanceType={maintenanceType}
      />
    </ModalDialog>
  );
};

export default EditMaintenanceTypeDialog;
